/**
 * Algolia Gallery & Interactions
 * Handles Slick carousel galleries and product card interactions
 */
(function algoliaGalleryInit() {
  'use strict';

  // Wait for jQuery to be available
  if (!window.jQuery) {
    // Check for jQuery periodically (max 50 attempts = 5 seconds)
    let attempts = 0;
    const checkJQuery = setInterval(function() {
      attempts++;
      if (window.jQuery) {
        clearInterval(checkJQuery);
        algoliaGalleryInit(); // Re-run the entire initialization
      } else if (attempts > 50) {
        clearInterval(checkJQuery);
      }
    }, 100);

    return;
  }

  const $ = window.jQuery;
  const config = window.algoliaThemeConfig || {};
  const utils = window.algoliaUtils || {};

  /**
   * Product JSON cache
   * Shared with algolia-utils but kept here for backward compatibility
   */
  window.__productJsonCache = window.__productJsonCache || {};

  /**
   * Build Slick gallery for a product card
   * @param {jQuery} $card - Product card element
   * @returns {Promise<jQuery>} Gallery element
   */
  function buildSlickGallery($card) {
    const $gal = $card.find('.qb-slick');
    const handle = $card.data('handle');
    const variantId = $card.data('variant-id');
    const isMobile = window.innerWidth < (config.breakpoints?.mobile || 768);

    if (!$gal.length || $gal.data('built') || !handle || !variantId) {
      return Promise.resolve($gal);
    }

    if (!utils.hasSlickLib || !utils.hasSlickLib()) {
      config.log?.('Slick library not available');
      return Promise.resolve($gal);
    }

    const isRTL = (document.documentElement.dir || '').toLowerCase() === 'rtl';
    if (!isRTL) $gal.attr('dir', 'ltr');

    return (utils.getProductJson ? utils.getProductJson(handle) : Promise.reject('utils not available'))
      .then(function(p) {
        const urls = utils.imagesForVariantFirst?.(p, variantId) || [];

        // Normalize URL for comparison: strip protocol, Shopify size suffixes, and query params
        var norm = function(u) {
          return String(u)
            .replace(/^https?:/, '')
            .replace(/_(?:pico|icon|thumb|small|compact|medium|large|grande|master|\d+x\d*|\d*x\d+)(\.(?:jpe?g|png|webp|gif))/i, '$1')
            .replace(/(\.(?:jpe?g|png|webp|gif))(?:\?.*)?$/i, '$1');
        };

        // Get the image already in the HTML slide and filter out any matching URL
        var existingKey = norm($gal.find('.qb-slide img').first().attr('src') || '');
        urls.filter(function(u) { return norm(u) !== existingKey; }).forEach(function(u) {
          var url = utils.ensureProtocol?.(u) || u;
          $gal.append('<div class="qb-slide"><img src="' + url + '" alt="" decoding="async" loading="lazy"></div>');
        });

        // Arrow button HTML
        const prev = '<button type="button" class="image-page-button image-page-button--previous ltr-icon" aria-label="Previous">' +
                     '<svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">' +
                     '<polyline points="15 18 9 12 15 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></button>';
        const next = '<button type="button" class="image-page-button image-page-button--next ltr-icon" aria-label="Next">' +
                     '<svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">' +
                     '<polyline points="9 6 15 12 9 18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></button>';

        // Get slick settings from config or use defaults
        const slickSettings = Object.assign({}, config.gallery?.slick || {}, {
          prevArrow: prev,
          nextArrow: next,
          rtl: isRTL,
        });

        // Initialize Slick
        $gal.one('init', function(event, slick) {
          $gal.data('built', true);
        }).slick(slickSettings);

        return $gal;
      })
      .catch(function(error) {
        config.error?.('Failed to build gallery:', handle, error);
        return $gal;
      });
  }

  /**
   * Ensure all slides are loaded for a card
   * @param {jQuery} $card - Product card element
   * @returns {Promise<boolean>} True if slides were added
   */
  function ensureCardSlides($card) {
    const $gal = $card.find('.qb-slick');

    if (!utils.isSlicked || !utils.isSlicked($gal)) {
      return Promise.resolve(false);
    }

    const inst = utils.slickInst ? utils.slickInst($gal) : null;
    if (!inst) return Promise.resolve(false);
    if (inst.slideCount >= 2) return Promise.resolve(true);

    const handle = $card.data('handle');
    const variantId = $card.data('variant-id');
    if (!handle || !variantId) return Promise.resolve(false);

    return (utils.getProductJson ? utils.getProductJson(handle) : Promise.reject('utils not available'))
      .then(function(p) {
        const urls = utils.imagesForVariantFirst?.(p, variantId) || [];
        if (!urls || urls.length <= 1) return false;

        // Normalize URL for comparison: strip protocol, Shopify size suffixes, and query params
        var norm = function(u) {
          return String(u)
            .replace(/^https?:/, '')
            .replace(/_(?:pico|icon|thumb|small|compact|medium|large|grande|master|\d+x\d*|\d*x\d+)(\.(?:jpe?g|png|webp|gif))/i, '$1')
            .replace(/(\.(?:jpe?g|png|webp|gif))(?:\?.*)?$/i, '$1');
        };

        var existingKey = norm($gal.find('.qb-slide img').first().attr('src') || '');
        urls.filter(function(u) { return norm(u) !== existingKey; }).forEach(function(u) {
          var url = utils.ensureProtocol?.(u) || u;
          $gal.slick('slickAdd', '<div class="qb-slide"><img src="' + url + '" alt="" decoding="async" loading="lazy"></div>');
        });

        if (utils.isSlicked && utils.isSlicked($gal)) {
          $gal.slick('setPosition');
        }

        config.log?.('Slides added for:', handle);
        return true;
      })
      .catch(function() {
        return false;
      });
  }

  /**
   * Observe product cards and build galleries when they enter viewport
   */
  (function observeAndBuild() {
    const IO = 'IntersectionObserver' in window ? window.IntersectionObserver : null;
    const seen = new WeakSet();

    function buildFor(cardEl) {
      if (seen.has(cardEl)) return;
      const $card = $(cardEl);
      buildSlickGallery($card).then(() => seen.add(cardEl));
    }

    if (IO) {
      const observerOptions = config.gallery?.observer || { rootMargin: '200px 0px 200px 0px' };

      const io = new IO((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            buildFor(entry.target);
          }
        });
      }, observerOptions);

      const attach = () => {
        const cards = document.querySelectorAll('.ais-Hits-item .product-block');
        cards.forEach(el => io.observe(el));
      };

      attach();
      new MutationObserver(attach).observe(document.body, { childList: true, subtree: true });
    } else {
      // Fallback for older browsers
      $(document).on('mouseenter', '.ais-Hits-item .product-block .image-cont', function() {
        buildFor($(this).closest('.product-block')[0]);
      });
    }
  })();

  /**
   * Hover swap - show second image on hover
   */
  $(document)
    .off('.qbHover')
    .on('mouseenter.qbHover', '.product-block .image-cont', function() {
      if (window.innerWidth < (config.breakpoints?.mobile || 768)) return;

      const $card = $(this).closest('.product-block');
      const $gal = $card.find('.qb-slick');

      if (!utils.isSlicked || !utils.isSlicked($gal)) return;

      ensureCardSlides($card).then(function() {
        const inst = utils.slickInst ? utils.slickInst($gal) : null;
        if (!inst || inst.slideCount < 2) return;
        $gal.slick('slickGoTo', 1, true);
      });
    })
    .on('mouseleave.qbHover', '.product-block .image-cont', function() {
      if (window.innerWidth < (config.breakpoints?.mobile || 768)) return;

      const $gal = $(this).find('.qb-slick');
      if (!utils.isSlicked || !utils.isSlicked($gal)) return;

      $gal.slick('slickGoTo', 0, true);
    });

  /**
   * Gallery image click handler - navigate to PDP
   * Only trigger when clicking on images/slides, not buttons or during drag
   */
  $(document)
    .off('.qbImageClick')
    .on('click.qbImageClick', '.ais-Hits-item .qb-slick .qb-slide, .ais-Hits-item .qb-slick img', function(e) {
      // Don't navigate if clicking on a button or control element
      if ($(e.target).closest('.image-page-button, .slick-dots, button, a.btn').length > 0) {
        return;
      }

      const $gal = $(this).closest('.qb-slick');
      const $list = $gal.find('.slick-list');

      // Don't navigate if gallery is being dragged
      if ($list.hasClass('dragging')) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // Get product URL from the card's link
      const $card = $gal.closest('.product-block');
      const $link = $card.find('.product-link--inline');
      const url = $link.attr('href');

      if (url) {
        window.location.href = url;
      }
    });

  /**
   * Arrow button click handler
   */
  $(document)
    .off('.qbArrow')
    .on('click.qbArrow', '.qb-slick .image-page-button', function(e) {
      e.preventDefault();
      e.stopPropagation();

      const $gal = $(this).closest('.qb-slick');
      const $card = $(this).closest('.product-block');

      if (!utils.isSlicked || !utils.isSlicked($gal)) return;

      ensureCardSlides($card).then(function() {
        const inst = utils.slickInst ? utils.slickInst($gal) : null;
        if (!inst) return;

        const dir = $(e.currentTarget).hasClass('image-page-button--next') ? 1 : -1;
        const curr = inst.currentSlide || 0;
        let next = curr + dir;

        // Loop around
        if (next < 0) next = inst.slideCount - 1;
        if (next >= inst.slideCount) next = 0;

        $gal.slick('slickGoTo', next, true);
      });
    });
})();
